import { ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Alert, Button, Modal } from './ui';

/** An area of an image, in the image's own pixels. */
export interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** An image file opened in the browser. Revoke `url` once it's no longer shown. */
export interface LoadedImage {
  image: HTMLImageElement;
  url: string;
  name: string;
}

interface Point {
  x: number;
  y: number;
}

const STAGE_PADDING = 24;
const MAX_ZOOM = 5;
/** Zooming stops once the crop is this many image pixels wide. */
const MIN_CROP_WIDTH = 200;
/** How far an arrow key moves the image, in screen pixels. */
const KEY_STEP = 20;

export async function loadImage(file: File): Promise<LoadedImage> {
  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    // The load event rather than decode(), which doesn't settle while the tab is in the background.
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('The image couldn’t be opened'));
      image.src = url;
    });
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  return { image, url, name: file.name };
}

/** The largest area of the image with this aspect ratio (width ÷ height), in the middle. */
export function centeredCrop(image: HTMLImageElement, aspect: number): Crop {
  const { naturalWidth, naturalHeight } = image;
  const width = Math.min(naturalWidth, naturalHeight * aspect);
  const height = width / aspect;
  return { x: (naturalWidth - width) / 2, y: (naturalHeight - height) / 2, width, height };
}

/** Whether the whole image already has this aspect ratio, give or take 1%. */
export const hasAspect = (image: HTMLImageElement, aspect: number) => Math.abs(image.naturalWidth / image.naturalHeight / aspect - 1) <= 0.01;

/**
 * Draws the cropped area as a JPEG at most `maxWidth` pixels wide, lowering the quality until it's under `maxBytes`.
 * Drawing it again also leaves behind the photo's metadata, such as where it was taken.
 */
export async function renderCrop(image: HTMLImageElement, crop: Crop, maxWidth: number, maxBytes: number): Promise<Blob> {
  const width = Math.max(1, Math.round(Math.min(maxWidth, crop.width)));
  const height = Math.max(1, Math.round((width * crop.height) / crop.width));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser couldn’t prepare the image. Try another browser.');
  // JPEG has no transparency, so transparent areas get a dark background.
  context.fillStyle = '#0f172a';
  context.fillRect(0, 0, width, height);
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);

  let blob: Blob | null = null;
  for (const quality of [0.86, 0.76, 0.66]) {
    blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= maxBytes) break;
  }
  if (!blob) throw new Error('This browser couldn’t prepare the image. Try another browser.');
  return blob;
}

/** A crop window with a frame fixed at `aspect`: people drag and zoom the image to choose what's inside the frame. */
export function ImageCropModal({
  source,
  aspect,
  title,
  description,
  maxWidth,
  minSharpWidth,
  saving,
  onCancel,
  onConfirm,
}: {
  source: LoadedImage;
  /** Width ÷ height. */
  aspect: number;
  title: string;
  description: string;
  /** The widest the crop is saved at. */
  maxWidth: number;
  /** Crops narrower than this get a warning that they may look blurry. */
  minSharpWidth: number;
  saving: boolean;
  onCancel(): void;
  onConfirm(crop: Crop): void;
}) {
  const { naturalWidth, naturalHeight } = source.image;
  const base = centeredCrop(source.image, aspect);
  const maxZoom = Math.max(1, Math.min(MAX_ZOOM, base.width / MIN_CROP_WIDTH));

  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState<Point>({ x: naturalWidth / 2, y: naturalHeight / 2 });
  const [stage, setStage] = useState({ width: 0, height: 0 });
  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; start: Point; center: Point } | null>(null);
  const onWheel = useRef<(event: WheelEvent) => void>(() => {});

  const cropWidth = base.width / zoom;
  const cropHeight = base.height / zoom;
  const frameHeight = Math.max(0, Math.min(stage.height - STAGE_PADDING * 2, (stage.width - STAGE_PADDING * 2) / aspect));
  const frameWidth = frameHeight * aspect;
  // Screen pixels per image pixel.
  const scale = cropWidth > 0 ? frameWidth / cropWidth : 0;

  /** Keeps the frame inside the image. */
  const clamp = (point: Point, atZoom: number): Point => {
    const width = base.width / atZoom;
    const height = base.height / atZoom;
    return {
      x: Math.min(naturalWidth - width / 2, Math.max(width / 2, point.x)),
      y: Math.min(naturalHeight - height / 2, Math.max(height / 2, point.y)),
    };
  };

  const zoomTo = (next: number) => {
    const value = Math.min(maxZoom, Math.max(1, next));
    setZoom(value);
    setCenter((current) => clamp(current, value));
  };

  useEffect(() => {
    onWheel.current = (event) => {
      event.preventDefault();
      zoomTo(zoom * Math.exp(-event.deltaY / 400));
    };
  });

  useEffect(() => {
    const element = stageRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setStage({ width: rect.width, height: rect.height });
    });
    observer.observe(element);
    // React's wheel events can't prevent the page from scrolling, so this one is added directly.
    const wheel = (event: WheelEvent) => onWheel.current(event);
    element.addEventListener('wheel', wheel, { passive: false });
    return () => {
      observer.disconnect();
      element.removeEventListener('wheel', wheel);
    };
  }, []);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, start: { x: event.clientX, y: event.clientY }, center };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId || !scale) return;
    setCenter(
      clamp(
        { x: current.center.x - (event.clientX - current.start.x) / scale, y: current.center.y - (event.clientY - current.start.y) / scale },
        zoom,
      ),
    );
  };

  const endDrag = () => {
    drag.current = null;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = KEY_STEP / (scale || 1);
    // The arrow keys move the image, the same way dragging does.
    const moves: Record<string, Point> = { ArrowLeft: { x: step, y: 0 }, ArrowRight: { x: -step, y: 0 }, ArrowUp: { x: 0, y: step }, ArrowDown: { x: 0, y: -step } };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setCenter((current) => clamp({ x: current.x + move.x, y: current.y + move.y }, zoom));
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomTo(zoom + 0.25);
    } else if (event.key === '-') {
      event.preventDefault();
      zoomTo(zoom - 0.25);
    }
  };

  const crop: Crop = { x: center.x - cropWidth / 2, y: center.y - cropHeight / 2, width: cropWidth, height: cropHeight };
  const outputWidth = Math.round(Math.min(maxWidth, cropWidth));
  const outputHeight = Math.round(outputWidth / aspect);

  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      description={description}
      footer={
        <>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} disabled={!scale} onClick={() => onConfirm(crop)}>
            Use this image
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div
          ref={stageRef}
          tabIndex={0}
          aria-label="Crop area. Drag the image or use the arrow keys to move it, and press plus or minus to zoom."
          className="relative h-[26rem] max-h-[55vh] cursor-grab touch-none overflow-hidden rounded-lg bg-slate-900 outline-none select-none focus-visible:ring-2 focus-visible:ring-brand-500 active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
        >
          {scale > 0 && (
            <>
              <img
                src={source.url}
                alt=""
                draggable={false}
                className="pointer-events-none absolute max-w-none"
                style={{
                  width: naturalWidth * scale,
                  height: naturalHeight * scale,
                  left: stage.width / 2 - center.x * scale,
                  top: stage.height / 2 - center.y * scale,
                }}
              />
              <div
                className="pointer-events-none absolute shadow-[0_0_0_9999px_rgb(15_23_42/0.6)] outline-2 outline-white"
                style={{ width: frameWidth, height: frameHeight, left: (stage.width - frameWidth) / 2, top: (stage.height - frameHeight) / 2 }}
              >
                {/* Thirds, to help place the subject. */}
                <div className="absolute inset-y-0 left-1/3 w-px bg-white/40" />
                <div className="absolute inset-y-0 left-2/3 w-px bg-white/40" />
                <div className="absolute inset-x-0 top-1/3 h-px bg-white/40" />
                <div className="absolute inset-x-0 top-2/3 h-px bg-white/40" />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" icon={<ZoomOut />} aria-label="Zoom out" disabled={zoom <= 1} onClick={() => zoomTo(zoom - 0.25)} />
          <input
            type="range"
            min={1}
            max={maxZoom}
            step={0.01}
            value={zoom}
            disabled={maxZoom <= 1}
            onChange={(event) => zoomTo(Number(event.target.value))}
            aria-label="Zoom"
            className="flex-1 accent-brand-600"
          />
          <Button size="sm" variant="ghost" icon={<ZoomIn />} aria-label="Zoom in" disabled={zoom >= maxZoom} onClick={() => zoomTo(zoom + 0.25)} />
        </div>
        <p className="text-xs text-slate-500">
          Saved at {outputWidth.toLocaleString()} × {outputHeight.toLocaleString()} pixels.
        </p>
        {outputWidth < minSharpWidth && (
          <Alert tone="amber">
            This area is only {Math.round(cropWidth).toLocaleString()} pixels wide, so it may look blurry on large screens. Zoom out, or choose a larger
            image.
          </Alert>
        )}
      </div>
    </Modal>
  );
}
